import {
  Badge,
  Box,
  Button,
  createStyles,
  Skeleton,
  Text,
} from "@mantine/core";
import {
  IconClipboardX,
  IconFileAlert,
  IconTrash,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import moment from "moment";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import {
  clearPendingActionsSkypoint,
  getPendingActionsSkypoint,
} from "../../../services/dashboard";
import store from "../../../store";
import {
  setSelectedDocModal,
  setUploadDocModal,
} from "../../../store/reducers/document";
import { decrypt } from "../../../utils";
import SkypointPendingActionsDrawer from "./SkypointPendingActionsDrawer";

const useStyles = createStyles(() => ({
  container: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    // Locked to match the To-do List panel so both columns line up.
    height: 640,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    borderBottom: "1px solid #F2F4F7",
    gap: 8,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  title: {
    fontFamily: "Inter",
    fontSize: 15,
    fontWeight: 600,
    color: "#101828",
    whiteSpace: "nowrap",
  },
  countBadge: {
    fontFamily: "Inter",
    fontWeight: 600,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    "::-webkit-scrollbar": { width: 6 },
    "::-webkit-scrollbar-thumb": {
      background: "#D0D5DD",
      borderRadius: 3,
    },
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    borderBottom: "1px solid #F9FAFB",
    cursor: "pointer",
    transition: "background 120ms ease",
    "&:hover": {
      background: "#FAFBFC",
    },
    "&:last-of-type": { borderBottom: "none" },
  },
  rowIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  rowPrimary: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    color: "#101828",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowSecondary: {
    fontFamily: "Inter",
    fontSize: 11.5,
    color: "#667085",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowDate: {
    fontFamily: "Inter",
    fontSize: 11,
    color: "#98A2B3",
    flexShrink: 0,
    marginLeft: 4,
  },
  empty: {
    padding: "32px 16px",
    textAlign: "center",
    fontFamily: "Inter",
    fontSize: 13,
    color: "#667085",
  },
  footer: {
    borderTop: "1px solid #F2F4F7",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "center",
  },
  viewAll: {
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: 500,
    color: "#7F56D9",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
}));

const TYPE_STYLE = {
  document: {
    Icon: IconFileAlert,
    iconColor: "#F04438",
    iconBg: "#FEF3F2",
  },
  note: {
    Icon: IconClipboardX,
    iconColor: "#F79009",
    iconBg: "#FFF6ED",
  },
};

const safeDecrypt = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  try {
    const d = decrypt(raw, false);
    if (typeof d === "string" && d.trim() && !d.startsWith("U2FsdGVkX1")) {
      return d.trim();
    }
  } catch (_) {
    /* fall through */
  }
  return null;
};

// A provider-level document is filed against the provider itself rather than
// one of their clients, staff or facilities.
const providerName = (provider) =>
  provider?.businessName ||
  provider?.name ||
  `${provider?.firstName || ""} ${provider?.lastName || ""}`.trim() ||
  null;

const labelForItem = (item) => {
  if (item?.type === "document") {
    const owner =
      item?.facility?.name ||
      safeDecrypt(item?.client?.name) ||
      item?.staff?.name ||
      providerName(item?.provider) ||
      "Business";
    return {
      primary: item?.name || "Expired document",
      secondary: owner,
      date: item?.expiry ? moment(item.expiry).format("MMM D, YYYY") : null,
    };
  }
  return {
    primary: item?.name || "Note pending approval",
    secondary: item?.noteId?.name || "Note",
    date: item?.noteDate
      ? moment(item.noteDate).format("MMM D, YYYY")
      : item?.createdAt
        ? moment(item.createdAt).format("MMM D, YYYY")
        : null,
  };
};

// Every expired document — client, staff, facility or provider — opens the same
// upload/preview modal; it works out which API to use from the record itself.
const handleDocumentClick = (item) => {
  store.dispatch(
    setSelectedDocModal({
      ...item,
      userId:
        item?.client?.id ||
        item?.staff?.id ||
        item?.facility?.id ||
        item?.provider?.id ||
        item?.provider?._id,
    })
  );
  store.dispatch(setUploadDocModal(true));
};

const SkypointPendingAction = ({ containerClassName }) => {
  const { classes, cx } = useStyles();
  const [
    drawerOpened,
    { open: openDrawer, close: closeDrawer },
  ] = useDisclosure(false);
  const [clearing, setClearing] = useState(false);

  const {
    dashboardPendingActions,
    dashboardPendingActionsLoading,
    dashboardPendingActionsCount,
  } = useSelector((state) => state?.dashboard);

  const items = dashboardPendingActions || [];
  const totalCount = dashboardPendingActionsCount || items.length || 0;
  const PREVIEW_LIMIT = 8;
  const previewItems = items.slice(0, PREVIEW_LIMIT);
  const hasMore = items.length > PREVIEW_LIMIT || totalCount > previewItems.length;

  const handleClearAll = async () => {
    if (totalCount === 0) return;
    const confirmed = await swal({
      title: "Clear all pending actions?",
      text: `Dismiss all ${totalCount} items from your Skypoint Pending Actions panel. The underlying notes and documents stay in each provider's Ogiso Core — only your contractor view is cleared.`,
      icon: "warning",
      buttons: ["Cancel", "Clear all"],
      dangerMode: true,
    });
    if (!confirmed) return;
    setClearing(true);
    const result = await clearPendingActionsSkypoint();
    setClearing(false);
    if (result) {
      await getPendingActionsSkypoint();
    }
  };

  return (
    <Box className={cx(classes.container, containerClassName)}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Text className={classes.title}>Pending Actions</Text>
          {dashboardPendingActionsLoading ? (
            <Skeleton width={28} height={20} radius="xl" />
          ) : (
            totalCount > 0 && (
              <Badge
                color="red"
                variant="filled"
                size="sm"
                radius="xl"
                className={classes.countBadge}
              >
                {totalCount}
              </Badge>
            )
          )}
        </Box>
        <Button
          size="xs"
          variant="subtle"
          color="red"
          leftIcon={<IconTrash size={14} />}
          loading={clearing}
          disabled={totalCount === 0 || clearing || dashboardPendingActionsLoading}
          onClick={handleClearAll}
        >
          Clear all
        </Button>
      </Box>

      <Box className={classes.list}>
        {dashboardPendingActionsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Box key={i} className={classes.row}>
              <Skeleton circle width={32} height={32} />
              <Box className={classes.rowText}>
                <Skeleton height={12} width="60%" />
                <Skeleton height={10} width="40%" mt={4} />
              </Box>
            </Box>
          ))
        ) : previewItems.length === 0 ? (
          <Box className={classes.empty}>No pending actions across your providers.</Box>
        ) : (
          previewItems.map((item) => {
            const styling = TYPE_STYLE[item?.type] || TYPE_STYLE.document;
            const Icon = styling.Icon;
            const { primary, secondary, date } = labelForItem(item);
            const clickable = item?.type === "document";
            return (
              <Box
                key={`${item?.type}-${item?._id || item?.id}`}
                className={classes.row}
                onClick={clickable ? () => handleDocumentClick(item) : undefined}
                style={{ cursor: clickable ? "pointer" : "default" }}
              >
                <Box
                  className={classes.rowIcon}
                  sx={{ backgroundColor: styling.iconBg }}
                >
                  <Icon size={16} color={styling.iconColor} />
                </Box>
                <Box className={classes.rowText}>
                  <Text className={classes.rowPrimary} title={primary}>
                    {primary}
                  </Text>
                  <Text className={classes.rowSecondary} title={secondary}>
                    {secondary}
                  </Text>
                </Box>
                {date && <Text className={classes.rowDate}>{date}</Text>}
              </Box>
            );
          })
        )}
      </Box>

      {hasMore && !dashboardPendingActionsLoading && (
        <Box className={classes.footer}>
          <Text className={classes.viewAll} onClick={openDrawer}>
            View all ({totalCount})
          </Text>
        </Box>
      )}

      <SkypointPendingActionsDrawer opened={drawerOpened} close={closeDrawer} />
    </Box>
  );
};

export default SkypointPendingAction;
